import { Controller } from "@hotwired/stimulus"

// Auto-scales text elements to fit their containers.
// Uses ResizeObserver to react to viewport/container size changes.
export default class extends Controller {
  connect() {
    this.scaleAll()
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => {
        requestAnimationFrame(() => this.scaleAll())
      })
      this.observer.observe(this.element)
    } else {
      // Fallback for Smart TVs without ResizeObserver
      window.addEventListener('resize', this.boundResize = () => this.scaleAll())
      window.addEventListener('load', () => this.scaleAll())
    }
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize)
    }
  }

  scaleAll() {
    this.fitDigits()
    this.fitKpis()
    this.fitTitles()
    this.fitBarValues()
    this.fitBarLabels()
  }

  // Scale odometer digit numbers to fit their .mwc-digit container
  fitDigits() {
    this.element.querySelectorAll('.mwc-digit').forEach(digit => {
      const h = digit.clientHeight
      if (h === 0) return
      // Each digit-number should fill ~75% of the container height
      const fontSize = h * 0.75
      digit.querySelectorAll('.mwc-digit-number').forEach(num => {
        num.style.fontSize = `${fontSize}px`
      })
    })
  }

  // Scale KPI values relative to their quadrant height
  fitKpis() {
    this.element.querySelectorAll('.mwc-kpi-value').forEach(kpi => {
      const quadrant = kpi.closest('.mwc-quadrant')
      if (!quadrant) return
      const h = quadrant.clientHeight
      if (h === 0) return
      // KPI value ~20% of quadrant height
      kpi.style.fontSize = `${h * 0.2}px`
    })
  }

  // Scale bar values to fit within bar width (sized for 4 digits)
  fitBarValues() {
    this.element.querySelectorAll('.mwc-bar').forEach(bar => {
      const value = bar.querySelector('.mwc-bar-value')
      if (!value) return
      const barWidth = bar.clientWidth
      if (barWidth === 0) return
      // Target: 4 digits fit comfortably with some padding
      const fontSize = barWidth / 3.2
      value.style.fontSize = `${Math.max(fontSize, 8)}px`
    })
  }

  // Detect overlapping bar labels and rotate them when needed
  fitBarLabels() {
    const labels = Array.from(this.element.querySelectorAll('.mwc-bar-label'))
    if (labels.length < 2) return

    // Reset first to measure natural widths
    labels.forEach(el => el.classList.remove('mwc-bar-label-rotated'))

    // Check if any adjacent labels overlap
    let overlapping = false
    for (let i = 0; i < labels.length - 1; i++) {
      const a = labels[i].getBoundingClientRect()
      const b = labels[i + 1].getBoundingClientRect()
      if (a.right > b.left) {
        overlapping = true
        break
      }
    }

    if (overlapping) {
      labels.forEach(el => el.classList.add('mwc-bar-label-rotated'))
    }
  }

  // Scale title/subtitle if they overflow the header
  fitTitles() {
    const header = this.element.querySelector('.mwc-header-left')
    if (!header) return
    const maxW = header.clientWidth
    if (maxW === 0) return

    header.querySelectorAll('.mwc-title, .mwc-subtitle').forEach(el => {
      // Reset to CSS value first
      el.style.fontSize = ''
      // If it overflows, shrink until it fits
      let size = parseFloat(getComputedStyle(el).fontSize)
      while (el.scrollWidth > maxW && size > 12) {
        size -= 1
        el.style.fontSize = `${size}px`
      }
    })
  }
}
